import { FunctionComponent, useEffect, useState } from 'react';
import { Button, IconButton, Paper, Switch, Dialog } from '../../tailwindcomponents';
import { palette } from '../../theme/palette';
import WarrantyPDF from './WarrantyPDF';
import { DatePicker } from '../../MaterialUiComponents';
import { Autocomplete, TextField, Typography, Input, Tooltip, Select } from '../../tailwindcomponents';
import IcelandairLogo from '../../assets/icelandairLogo.svg';
import { Close, Comment, AddComment, AttachMoney, AccessTime, Save, Edit, Check, Delete, Upload, Download, Description, Add, FolderDelete as FolderDeleteIcon } from '../../components/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducer';
import { dictionary, comment, warrantyMxiData, warrantyRequest, WarrantyMaintenerPart, actionItem, pdfObj } from '../../types/warranty';
import WarrantyApi from './../../api/warranty';
import { redirectToMxi } from './../../config/mxiProvider';
import { claimClassificationOptions, claimResultOptions, claimResultRejectedOptions, claimStatusOptions } from '../../properties/warrantiesProperties';
import ProfileImg from '../../components/Images/ProfileImg';

type WarrantyProps = {
    cageCodes: dictionary[]
    warrantyId: number | null
    returnToOverview: Function
    setLoading: Function
    loading: boolean
    urlBarcode: string,
    setTabValue: Function,
    refreshOverview: Function,
    allWarranties: warrantyRequest[]
}

// This page contains a single warranty request including fault data. On new request it takes input of fault barcode to fetch fault data for the request
const Warranty: FunctionComponent<WarrantyProps> = ({ cageCodes, warrantyId, returnToOverview, setLoading, loading, urlBarcode, setTabValue, refreshOverview, allWarranties}) => {
    const store = useSelector((state: RootState) => state);
    const [commentDialogOpen, setCommentDialogOpen] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [faultBarcode, setFaultBarcode] = useState<string>("");
    const [faultFetched, setFaultFetched] = useState<boolean>(false);
    const [errorText, setErrorText] = useState("");
    const [selectedClassification, setSelectedClassification] = useState<string>("");
    const [selectedStatus, setSelectedStatus] = useState<string>("In process");
    const [newPart, setNewPart] = useState<boolean>(false);
    const [selectedCageCode, setSelectedCageCode] = useState<dictionary>();
    const [warrantyMxiData, setWarrantyMxiData] = useState(Array<warrantyMxiData>());
    const [warrantyRequest, setWarrantyRequest] = useState<warrantyRequest>();
    const [newWarrantyId, setNewWarrantyId] = useState<number | null>(warrantyId);
    const [comments, setComments] = useState<comment[]>();
    const [reasonForRemoval, setReasonForRemoval] = useState("");
    const [actionItems, setActionItems] = useState<actionItem[]>();
    const [shippingCost, setShippingCost] = useState<string>("");
    const [actualHours, setActualHours] = useState<string>("");
    const [actualMinutes, setActualMinutes] = useState<string>("");
    const [hourlyRate, setHourlyRate] = useState<number>(0);
    const [commentToAdd, setCommentToAdd] = useState<string>("");
    const [createdDate, setCreatedDate] = useState<Date>(new Date());
    const [totalPartAmount, setTotalPartAmount] = useState<string>("");
    const [selectedClaimResult, setSelectedClaimResult] = useState<warrantyRequest["claim_request_type"]>(null);
    const [poNumber, setPoNumber] = useState<string | null>(null);
    const [partToEdit, setPartToEdit] = useState<number>(-1);
    const [saved, setSaved] = useState<boolean>(true);
    const [selectedPartToDelete, setSelectedPartToDelete] = useState<WarrantyMaintenerPart>();
    const [isBeingFetched, setIsBeingFetched] = useState<boolean>(true);
    const [pdfData, setPdfData] = useState<pdfObj[]>([]);
    const [deleteWarrantyRequestDialogOpen, setDeleteWarrantyRequestDialogOpen] = useState<boolean>(false);
    const [deletedReason, setDeletedReason] = useState<string>("");
    const [maintenerParts, setMaintenerParts] = useState<WarrantyMaintenerPart[]>([]);
    const [editPart, setEditPart] = useState<WarrantyMaintenerPart>({} as WarrantyMaintenerPart);

    const toValidDate = (value: Date | string | null | undefined): Date => {
        const parsedDate = value instanceof Date ? value : new Date(value ?? '');
        return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    };

    const searchFaultByBarcode = (barcode: string) => {
        const warrantyAlreadyExists = faultExistsValidation(barcode);
        if (barcode.length > 0 && !warrantyAlreadyExists) {
            setLoading(true);
            WarrantyApi.GetFaultByBarcode(barcode).then(res => {
                if (res.status === true) {
                    setWarrantyRequest(res.data);
                    setWarrantyMxiData(res.mxiData);
                    setReasonForRemoval(res.data.reason_for_removal);
                    setFaultFetched(true);
                    setActionItems(res.actionItems);
                    setActualHours(res.data.actual_manhours_worked_total_hrs);
                    setActualMinutes(res.data.actual_manhours_worked_total_min);
                    let tempPartTotal = 0;
                    res.mxiData.forEach(part => {
                        tempPartTotal = tempPartTotal + part.unit_price
                    });
                    setTotalPartAmount(tempPartTotal.toString());
                } else {
                    setErrorText(res.message);
                }
                setLoading(false);
            });

        }
    }

    const getWarrantyPdfStatus = () => {
        if (warrantyId) {
            //Fetching pdf's when page loads, prepare pdf's download for faster experiance.
            WarrantyApi.DownloadPDF(warrantyId).then(res => {
                if (res.status === true) {
                    setPdfData(res.data);
                }
            });
        }
    }

    useEffect(() => {
        getWarrantyPdfStatus();
        if (newWarrantyId !== null) {
            refresh(newWarrantyId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (urlBarcode.length > 0) {
            setFaultBarcode(urlBarcode);
            searchFaultByBarcode(urlBarcode);
        }
        else {
            setIsBeingFetched(false);
        }
    }, []);

    useEffect(() => {
        setSaved(false);
    }, [createdDate, selectedCageCode, selectedClassification, selectedStatus, selectedClaimResult, shippingCost, actualHours, reasonForRemoval]);

    const closeClaim = () => {
        if (warrantyId === null) {
            setFaultBarcode("");
            setFaultFetched(false);
        }
        else {
            returnToOverview();
        }
    }
    const refresh = (id: number) => {
        setLoading(true);
        WarrantyApi.getWarrantyRequestById(id).then(res => {
            setMaintenerParts(res.maintenerParts);
            setSelectedClaimResult(res.data.claim_request_type);
            setWarrantyRequest(res.data);
            setWarrantyMxiData(res.mxiData);
            setSelectedClassification(res.data.claim_classification_type);
            setNewPart(res.data.is_part_purchased);
            setSelectedStatus(res.data.request_status);
            setSelectedCageCode(cageCodes.find(code => code.id === res.data.cage_code_id));
            setShippingCost(res.data.shipping_claimed_cost.toString());
            setReasonForRemoval(res.data.reason_for_removal);
            setFaultFetched(true);
            setComments(res.comments);
            setActionItems(res.actionItems);
            setHourlyRate(res.data.mechanic_hourly_rate);
            setLoading(false);
            setCreatedDate(toValidDate(res.data.created_date));
            setActualHours(res.data.actual_manhours_worked_total_hrs);
            setActualMinutes(res.data.actual_manhours_worked_total_min);
            setFaultBarcode(res.data.fault_barcode);
            setPoNumber(res.data.po_number);
            let tempPartTotal = 0;
            res.mxiData.forEach(part => {
                tempPartTotal = tempPartTotal + part.unit_price
            });
            setTotalPartAmount(tempPartTotal.toString());
            setSaved(true);
        });
    }

    const createWarranty = () => {
        const warrantyAlreadyExists = faultExistsValidation(faultBarcode);
        if (selectedCageCode !== undefined && hourlyRate !== undefined && selectedClassification !== "" && actualHours !== "" && actualMinutes !== "" && !warrantyAlreadyExists) {
            const isDUmmy = warrantyRequest?.is_dummy ? warrantyRequest?.is_dummy : false;
            WarrantyApi.createWarrantyRequest(store.user.me.user_id, faultBarcode, selectedCageCode.id, selectedStatus, selectedClassification, newPart, +shippingCost, reasonForRemoval, hourlyRate, createdDate, actualHours, actualMinutes, poNumber, isDUmmy, selectedClaimResult).then(res => {
                if (res.status === false) {
                    setErrorText("Database error");
                } else {
                    setSaved(true);
                    setErrorText("");
                    setNewWarrantyId(res.id);
                    refresh(res.id);
                }
            });
        } else {
            setErrorText("Please fill in all fields before creating claim request and make sure to claim the manhours");
        }
    }
    const updateWarranty = () => {
        if (selectedCageCode !== undefined && warrantyRequest !== undefined && newWarrantyId !== null) {
            WarrantyApi.updateWarrantyRequest(newWarrantyId, store.user.me.user_id, faultBarcode, selectedCageCode.id, selectedStatus, selectedClassification, newPart, +shippingCost, reasonForRemoval, createdDate, actualHours, actualMinutes, selectedClaimResult, poNumber, hourlyRate).then(res => {
                if (newWarrantyId !== null) {
                    setSaved(true);
                    refresh(newWarrantyId);
                }
            });
        }
    }
    const onCommentDialogClose = (status: boolean) => {
        if (status === true && warrantyRequest !== undefined) {
            WarrantyApi.AddComment(warrantyRequest.warranty_request_id, store.user.me.user_id, commentToAdd).then(res => {
                refresh(warrantyRequest.warranty_request_id);
            });
        }
        setCommentDialogOpen(false);
        setCommentToAdd("");
    }
    const deleteComment = (commentToDelete: comment) => {
        if (warrantyRequest !== undefined) {
            WarrantyApi.deleteComment(commentToDelete, store.user.me.user_id).then(res => {
                refresh(warrantyRequest.warranty_request_id);
            });
        }
    }

    const onEditPart = (part: WarrantyMaintenerPart, index: number) => {
        setEditPart(part);
        setPartToEdit(index);
    }
    const onSavePart = () => {
        if (editPart.is_new_part !== true) {
            WarrantyApi.updatePartRequest(editPart).then(res => { });
        }
        else {
            WarrantyApi.CreatePartRequest(editPart).then(res => { });
        }
        if (newWarrantyId !== null) {
            refresh(newWarrantyId);
        }
        setPartToEdit(-1);
    }
    const onDeletePart = (part: WarrantyMaintenerPart) => {
        setSelectedPartToDelete(part);
        setDeleteDialogOpen(true);
    }
    const onConfirmDelete = (status: boolean) => {
        if (status === true) {
            if (selectedPartToDelete !== undefined) {
                WarrantyApi.deletePartRequest(selectedPartToDelete.id, store.user.me.user_id).then(res => {
                    if (newWarrantyId !== null) {
                        refresh(newWarrantyId);
                    }
                });
            }
        }
        setDeleteDialogOpen(false);
    }

    const handleFileChange = (e: any) => {
        if (e.target.files[0]) {
            const file: File = e.target.files[0];
            const fileData = new FormData();
            fileData.append("file_name", file.name);
            fileData.append("form_file", file);
            if (warrantyId) {
                WarrantyApi.SavePDF(fileData, warrantyId).then(res => {
                    getWarrantyPdfStatus();
                });
            }
        }
    };

    const onDownloadPDF = () => {
        if (warrantyId) {
            pdfData.forEach(pdf => {
                const linkSource = `data:application/pdf;base64,${pdf.base_64_string}`;
                const downloadLink = document.createElement("a");
                const fileName = pdf.name;
                downloadLink.href = linkSource;
                downloadLink.download = fileName;
                downloadLink.click();
            });
        }
    }

    const onDeleteWarrantyPDF = (pdf: pdfObj) => {
        WarrantyApi.DeletePDF(pdf.pdf_id).then(res => {
            getWarrantyPdfStatus();
        });
    }

    const createDummyWarranty = () => {
        const warrantyAlreadyExists = faultExistsValidation(faultBarcode);
        if(!warrantyAlreadyExists){
            setFaultFetched(true);
            const tempWarranty: warrantyRequest = {
                warranty_request_id: -1,
                warranty_id: -1,
                po_number: "",
                created_date: new Date(),
                created_date_text: "",
                created_by_employee_id: -1,
                created_by_employee_name: "",
                claim_request_type: null,
                last_modified_date: new Date(),
                last_modified_by_employee_id: -1,
                fault_barcode: "",
                cage_code_id: -1,
                cage_code_text: "",
                warranty_status: "",
                request_status: "",
                claim_classification_type: "",
                is_part_purchased: false,
                part_claimed_cost: 0,
                claimed_man_hrs: 0,
                shipping_claimed_cost: 0,
                deleted_reason: "",
                visible: false,
                mxiData: [],
                manhours_claimed_cost: 0,
                actual_manhours_worked_total: 0,
                actual_manhours_worked_total_hrs: 0,
                actual_manhours_worked_total_min: 0,
                actual_manhours_worked_total_text: "",
                actual_manhours_worked_total_cost: 0,
                reason_for_removal: "",
                mechanic_hourly_rate: 0,
                total_claimed_cost: 0,
                mxi_actual_worked_manhours_hrs: 0,
                mxi_actual_worked_manhours_min: 0,
                last_comment: "",
                part_numbers: [],
                tail_number: "",
                is_dummy: true,
                has_attachment: false,
            }
            setWarrantyRequest(tempWarranty);
        }
    }

    const onDeleteWarrantyRequest = () => {
        WarrantyApi.DeleteWarrantyRequest(warrantyId, deletedReason).then(res => {
            if (res.status === true) {
                setTabValue(0);
                refreshOverview();
            }
        });
    }

    const createNewPart = () => {
        const newPart = {
            id: -1,
            warranty_request_id: warrantyId,
            created_date: new Date(),
            created_by_employee_id: -1,
            last_modified_date: new Date(),
            last_modified_by_employee_id: -1,
            deleted_reason: "",
            part_tsn_hours: 0,
            part_tsn_minutes: 0,
            inv_no_id: -1,
            part_name: "",
            part_number: "",
            part_cost_usd: -1,
            purchase_order_no: "",
            part_serial_no: "",
            is_new_part: true,
            visible: true,
            fault_start_date: null,
        } as WarrantyMaintenerPart;
        const allParts = [...maintenerParts, newPart];
        setMaintenerParts(allParts);
    }

    const faultExistsValidation = (barcode: string) =>{
        if (barcode.length > 0) {
            if(allWarranties.find(warranty => warranty.fault_barcode === barcode) !== undefined){
                setErrorText("This fault barcode already exists in the system");
                return true;
            }
            else{
                setErrorText("");
                return false;
            }
        } else {
            setErrorText("Please provide barcode");
            return true;
        }
    }
    
    return (
        <div>
            {faultFetched === false && loading === false ?
                <div className="flex flex-wrap w-full" style={{ visibility: isBeingFetched ? "hidden" : "visible" }}>
                    <div className="w-full">
                        <Input multiline={false} help_text={errorText === "" ? "Type / paste in barcode" : errorText} error={errorText !== ""} label_text={"Fault barcode"} value={faultBarcode} onTextChange={(text: string) => setFaultBarcode(text)} type="text" width={40} />
                    </div>
                    <div className="w-auto">
                        <Tooltip title={"Create warranty item from mxi Data"} type='secondary'>
                            <Button id="fetchBtn" variant="contained" color="primary" size="medium" onClick={() => searchFaultByBarcode(faultBarcode)}>Fetch fault</Button>
                        </Tooltip>
                    </div>
                    <div className="w-auto">
                        <Tooltip title={"Create Dummy item"} type='secondary'>
                            <Button id="createBtn" variant="contained" color="primary" size="medium" className="ml-4" onClick={() => createDummyWarranty()}>Create fault</Button>
                        </Tooltip>
                    </div>
                    <Typography text={errorText} color='error' margin={[1, 0, 0, 2]} />
                </div>
                :
                faultFetched === false && loading === true ?
                    <div></div>
                    :
                    warrantyRequest !== undefined &&
                    <div>
                        <div className="mb-4"></div>
                        <div className="flex flex-wrap justify-center w-full">
                            <div className="w-7/12">
                                <Typography color='primary' variant='h1' text="Warranty Claim" align='left' />
                            </div>
                            <div className="w-1/3">
                                <img src={IcelandairLogo} className="h-[50px] bottom-[5px] relative" alt="Logo" />
                            </div>
                            <div className="w-1/12">
                                <IconButton onClick={() => { closeClaim() }} color="default" >
                                    <Close />
                                </IconButton>
                            </div>
                        </div>
                        <hr />
                        <div className="flex flex-wrap w-full">
                            <div className="w-auto">
                                <Tooltip title={`Download to computer this warranty request as pdf`} type='primary' placement="top-start">
                                    <WarrantyPDF mxiData={warrantyMxiData} warrantyRequest={warrantyRequest} actionItems={actionItems} />
                                </Tooltip>
                            </div>
                            <div className="w-auto">
                                <Button startIcon={<Save />} className="ml-4" variant="contained" color={saved ? "primary" : "secondary"} size="medium" onClick={() => { newWarrantyId !== null ? updateWarranty() : createWarranty() }}>Save</Button>
                            </div>
                            {(warrantyId !== null && warrantyId !== undefined) &&
                                <div className="w-auto">
                                    <div className="flex flex-row flex-wrap">
                                        <div className="w-auto mt-0">
                                            <label htmlFor={'fileUploadButton'}>
                                                <Tooltip title={`Upload a pdf to this warranty request`} type='primary' placement="top-start">
                                                    <span className="inline-flex cursor-pointer items-center gap-2 rounded bg-primary px-4 py-1.5 text-sm font-medium text-[color:var(--ds-accent-fg)] hover:bg-primary/90 ml-4">
                                                        <Upload />
                                                        Upload pdf
                                                    </span>
                                                </Tooltip>
                                            </label>
                                            <input id="fileUploadButton" className="hidden" type="file" onChange={(e: any) => { handleFileChange(e) }} />
                                        </div>
                                        {pdfData.length > 0 &&
                                            <div className="w-auto">
                                                <Tooltip title={`Download to computer the pdf's that have been uploaded to this warranty request`} type='primary' placement="top-start">
                                                    <Button startIcon={<Download />} className="ml-4" variant="contained" color={"primary"} size="medium" onClick={() => { onDownloadPDF() }}>Download  PDF</Button>
                                                </Tooltip>
                                            </div>
                                        }
                                        <div className="w-auto">
                                            <Tooltip title={`Delete this warranty request`} type='primary' placement="top-start">
                                                <Button startIcon={<Delete />} className="ml-4" variant="contained" color={"primary"} size="medium" onClick={() => { setDeleteWarrantyRequestDialogOpen(true) }}>Delete warranty</Button>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                        <div className="flex flex-wrap items-start w-full">
                            <div className="w-full lg:w-1/2">
                                <div className="flex flex-wrap w-full items-start justify-start gap-4 mt-2">
                                    <div className="w-full">
                                        {errorText !== "" && <Typography text={errorText} color='error' />}
                                        <div className="flex flex-row flex-wrap items-center w-full">
                                            <div className="w-auto" onClick={() => redirectToMxi(faultBarcode)}><Typography cursor="pointer" color="primary" text={faultBarcode} fontWeight="bold" /></div>
                                        </div>
                                        <div className="flex flex-row flex-wrap items-center w-full">
                                            <div className="w-auto"> <Typography text="Fix / Reimburse Part" /></div>
                                            <div className="w-auto">
                                                <Switch
                                                    checked={newPart}
                                                    onChange={() => { setNewPart(!newPart) }}
                                                    color="primary"
                                                    name="newPart"
                                                />
                                            </div>
                                            <div className="w-auto">
                                                <Typography text="New Part Purchased" />
                                            </div>
                                        </div>
                                        <div className="flex flex-row flex-wrap items-center w-full">
                                            <Typography display="inline" fontWeight="bold" text="MXI Manhours" />
                                            <Typography display="inline" text={": " + warrantyRequest.mxi_actual_worked_manhours_hrs + " hrs : " + warrantyRequest.mxi_actual_worked_manhours_min + " min"} />
                                        </div>
                                    </div>
                                    <div className="w-1/3">
                                        <DatePicker type="Date" selected={createdDate} label="Request created" onDateChange={(date: Date | null) => date && setCreatedDate(date)} />
                                    </div>
                                    <div className="w-1/3">
                                        <Autocomplete
                                            fullWidth
                                            noOptionsText="No Cage Code found"
                                            options={cageCodes}
                                            getOptionLabel={option => option.name}
                                            defaultValue={selectedCageCode}
                                            value={selectedCageCode}
                                            clearOnEscape
                                            autoHighlight
                                            onChange={(_e: any, newValue: dictionary | string | null) => newValue && typeof newValue !== 'string' ? setSelectedCageCode(newValue) : {}}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Cage Code" variant="standard" />
                                            )} />
                                    </div>
                                    <div className="w-1/3">
                                        <Select width={100} widthPercentage filter_by_text="Claim Classification" onSelectedItem={(e: any) => setSelectedClassification(e.target.value)} options={Object.values(claimClassificationOptions)} selectedItem={selectedClassification} />
                                    </div>
                                    <div className="w-1/3">
                                        <Select width={100} widthPercentage filter_by_text="Claim Status" onSelectedItem={(e: any) => setSelectedStatus(e.target.value)} options={Object.values(claimStatusOptions)} selectedItem={selectedStatus} />
                                    </div>
                                    <div className="w-1/3">
                                        <Select width={100} widthPercentage filter_by_text="Claim Result" onSelectedItem={(e: any) => setSelectedClaimResult(e.target.value)} options={["Not Claimed", "Rejected"].includes(selectedStatus) ? Object.values(claimResultRejectedOptions) : Object.values(claimResultOptions)} selectedItem={selectedClaimResult} />
                                    </div>
                                    <div className="w-1/3">
                                        <Input multiline={false} help_text={""} label_text={"Add PO Number"} value={poNumber === null ? "" : poNumber} onTextChange={(text: string) => { setPoNumber(text) }} type="text" width={100} />
                                    </div>
                                    <div className="w-1/4">
                                        <Input icon={<AttachMoney />} disabled multiline={false} help_text={""} label_text={"Parts total"} value={totalPartAmount} onTextChange={(text: string) => { setTotalPartAmount(text) }} type="number" width={100} />
                                    </div>
                                    <div className="w-1/4">
                                        <Input icon={<AttachMoney />} multiline={false} help_text={"Shipping Cost"} label_text={"Shipping Cost"} value={shippingCost} onTextChange={(text: string) => { setShippingCost(text) }} type="number" width={100} />
                                    </div>
                                    <div className="w-1/4">
                                        <Input icon={<AttachMoney />} multiline={false} help_text={"Hourly Rate"} label_text={"Hourly Rate"} value={hourlyRate} onTextChange={(text: number) => { setHourlyRate(text) }} type="text" width={100} />
                                    </div>
                                    <div className="w-1/3">
                                        <div className="flex flex-wrap w-full">
                                            <div className="w-1/2">
                                                <Input icon={<AccessTime />} textAlign="center" multiline={false} help_text={"Hours"} label_text={"Manhours"} value={actualHours} onTextChange={(text: string) => { setActualHours(text) }} type="number" width={100} />
                                            </div>
                                            <div className="w-1/6 text-gray-500 mt-6 font-bold text-center">:</div>
                                            <div className="w-1/3">
                                                <Input textAlign="center" multiline={false} help_text={"Minutes"} label_text={""} value={actualMinutes} onTextChange={(text: string) => { setActualMinutes(text) }} type="number" width={100} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="w-full">
                                        <Input multiline={true} help_text={"This is fetched from the Fault data originally but can be modified for the claim"} label_text={"Reason for Removal"} value={reasonForRemoval} onTextChange={(text: string) => { setReasonForRemoval(text) }} type="number" width={100} />
                                    </div>
                                    <div className="w-full">
                                        <Typography text="Actions taken" variant="caption" />
                                        {actionItems?.map(action => (
                                            <Paper variant="outlined" elevation={0}>
                                                <div className="flex flex-wrap items-center w-full">
                                                    <div className="w-1/6 md:w-1/12">
                                                        <figure className="m-2">
                                                            <img src={action.created_by_user_image} className="h-8 w-8 rounded-full" alt="user_image" />
                                                            <figcaption>{action.created_by_name.split(" ")[0]}</figcaption>
                                                        </figure>
                                                    </div>
                                                    <div className="w-5/6 md:w-11/12">
                                                        <Typography display="inline" text={action.action_text} variant="body1" />
                                                    </div>
                                                </div>
                                            </Paper>
                                        ))}
                                    </div>
                                    {pdfData.length > 0 &&
                                        <div>
                                            {pdfData.map(pdf => (
                                                <IconButton color="primary" onClick={() => onDeleteWarrantyPDF(pdf)}>
                                                    <Tooltip title={`Delete ${pdf.name}`} type='primary' placement="top-start">
                                                        <FolderDeleteIcon width={100} height={100} />
                                                    </Tooltip>
                                                </IconButton>
                                            ))
                                            }
                                        </div>
                                    }
                                    {(warrantyId !== null || newWarrantyId !== null) &&
                                        <div className="flex flex-wrap justify-center mt-4">
                                            <IconButton color="primary" onClick={() => { setCommentDialogOpen(true) }}><AddComment fontSize="1.75rem" /></IconButton>
                                        </div>
                                    }
                                    <div className="w-full">
                                        {comments?.map((comment, index) => (
                                            <Paper variant="elevation" elevation={5} className="pb-2 pl-6 w-full mb-6" style={{ backgroundColor: palette.accentBlue, color: palette.backgroundPaper }}>
                                                <div className="flex flex-wrap w-full">
                                                    <div className="w-11/12 flex flex-wrap items-center gap-2">
                                                        <Comment />
                                                        <Typography display="inline" text={comment.created_date_text} variant="caption" />
                                                        <ProfileImg imgKey={index} imgSize="xs" userName={store.employees.all_employees.find(s => s.Id === comment.created_by_employee_id)?.userName.split('@')[0]} />
                                                        <Typography display="inline" text={comment.comment} variant="body1" />
                                                    </div>
                                                    {
                                                        comment.system_generated === false &&
                                                        <div className="w-1/12">
                                                            <IconButton onClick={() => deleteComment(comment)} style={{ color: palette.backgroundPaper }}><Close /></IconButton>
                                                        </div>
                                                    }
                                                </div>
                                            </Paper>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <Dialog
                                visible={commentDialogOpen}
                                max_width="md"
                                title="Please type in your comment"
                                show_options={true}
                                strict={false}
                                onClose={(status: boolean) => { onCommentDialogClose(status) }}
                                context={
                                    <div className="flex flex-wrap justify-center">
                                        <Input multiline={true} help_text={"Type in your comment"} label_text={"Comment to be added"} value={commentToAdd} onTextChange={(text: string) => { setCommentToAdd(text) }} type="text" width={80} />
                                    </div>
                                } />
                            <Dialog
                                visible={deleteDialogOpen}
                                max_width="sm"
                                title=""
                                show_options={true}
                                strict={false}
                                onClose={(status: boolean) => onConfirmDelete(status)}
                                context={
                                    <div className="flex flex-wrap justify-center">
                                        <Typography text="Are you sure you want to remove this part from the Warranty Claim Request?" variant="h2" color="textPrimary" align="center" />
                                    </div>
                                } />
                            {/* Right side space */}
                            {warrantyMxiData.length !== undefined &&
                                <div className="flex flex-wrap w-full lg:w-1/2">
                                    <div className="w-1/2"><Typography margin={[0, 0, 2, 0]} align="center" variant="h2" text={"Aircraft: " + warrantyMxiData[0]?.aircraft !== undefined ? warrantyMxiData[0]?.aircraft : ""} /></div>
                                    <div className="w-1/2"><Typography align="center" variant="h2" text={"Manufactured: " + warrantyMxiData[0]?.aircraft_manufactured_date_text !== undefined ? warrantyMxiData[0]?.aircraft_manufactured_date_text : ""} /></div>
                                    {maintenerParts.map((part, index) => (
                                        <Paper variant="elevation" className="p-6 w-full mb-6 bg-[color:var(--ds-border)]" elevation={5}>
                                            <IconButton onClick={() => onDeletePart(part)} className="absolute right-10"> <Delete color="error" className="absolute" /> </IconButton>
                                            <div className="flex flex-wrap gap-2 w-full">
                                                <div className="w-full">
                                                    <Input
                                                        icon={<Description />}
                                                        disabled={partToEdit === index ? false : true}
                                                        multiline={false}
                                                        help_text={""}
                                                        label_text={"Part description"}
                                                        value={partToEdit === index ? editPart.part_name : part.part_name}
                                                        onTextChange={(text: string) => setEditPart({ ...editPart, part_name: text })}
                                                        type="text"
                                                        width={95}
                                                    />
                                                </div>
                                                <div className="w-1/2">
                                                    <div className="w-full">
                                                        <Input
                                                            icon={<Description />}
                                                            disabled={partToEdit === index ? false : true}
                                                            multiline={false}
                                                            help_text={""}
                                                            label_text={"Part number"}
                                                            value={partToEdit === index ? editPart.part_number : part.part_number}
                                                            onTextChange={(text: string) => setEditPart({ ...editPart, part_number: text })}
                                                            type="text"
                                                            width={95}
                                                        />
                                                    </div>
                                                    <div className="w-full">
                                                        <div className="w-full">
                                                            <Input
                                                                icon={<Description />}
                                                                disabled={partToEdit === index ? false : true}
                                                                multiline={false}
                                                                help_text={""}
                                                                label_text={"Serial number"}
                                                                value={partToEdit === index ? editPart.part_serial_no : part.part_serial_no}
                                                                onTextChange={(text: string) => setEditPart({ ...editPart, part_serial_no: text })}
                                                                type="text"
                                                                width={95}
                                                            />
                                                        </div>
                                                        <div className="w-full">
                                                            <Input
                                                                icon={<Description />}
                                                                disabled={partToEdit === index ? false : true}
                                                                multiline={false}
                                                                help_text={""}
                                                                label_text={"Purchase Order"}
                                                                value={partToEdit === index ? editPart.purchase_order_no : part.purchase_order_no}
                                                                onTextChange={(text: string) => setEditPart({ ...editPart, purchase_order_no: text })}
                                                                type="text"
                                                                width={95}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="w-1/2">
                                                    <div className="w-full">
                                                        <DatePicker
                                                            disabled={index === partToEdit ? false : true}
                                                            type="Date"
                                                            selected={(editPart.fault_start_date !== null && editPart.fault_start_date !== undefined) ? editPart.fault_start_date : null}
                                                            label={`Removal date`}
                                                            onDateChange={(date: Date) => setEditPart({ ...editPart, fault_start_date: date })}
                                                        />
                                                    </div>
                                                    <div className="w-full">
                                                        <Typography display="inline" text="Time since new: " variant="body1" fontWeight="bold" />
                                                        <Typography
                                                            display="inline"
                                                            text={(part.part_tsn_hours !== null ? part.part_tsn_hours : 0) + " hrs : " + (part.part_tsn_minutes !== null ? part.part_tsn_minutes : 0) + " min"}
                                                            variant="body1"
                                                        />
                                                    </div>
                                                    <div className="flex flex-wrap mt-1 w-full">
                                                        <div className="w-1/4">
                                                            <Input
                                                                disabled={index === partToEdit ? false : true}
                                                                icon={<AttachMoney />}
                                                                multiline={false}
                                                                help_text={""}
                                                                label_text={"Part cost"}
                                                                value={index === partToEdit ? editPart.part_cost_usd : part.part_cost_usd}
                                                                onTextChange={(text: string) => { setEditPart({ ...editPart, part_cost_usd: parseInt(text) }) }}
                                                                type="number"
                                                                width={100}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="w-auto">
                                                    {
                                                        index !== partToEdit ?
                                                            <IconButton size="medium" onClick={() => onEditPart(part, index)}>
                                                                <Edit color="primary" />
                                                            </IconButton>
                                                            :
                                                            <IconButton size="medium" onClick={() => { onSavePart() }}>
                                                                <Check className="text-green-600" />
                                                            </IconButton>
                                                    }
                                                </div>
                                            </div>
                                        </Paper>
                                    ))}
                                    <div className="flex flex-wrap justify-center">
                                        <IconButton color="primary" onClick={() => { createNewPart() }}><Add fontSize="1.75rem" /></IconButton>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>}
            <Dialog
                visible={deleteWarrantyRequestDialogOpen}
                max_width="sm"
                title="Are you sure you want to delete this warranty request?"
                show_options={true}
                strict={false}
                onClose={(status: boolean) => { status ? onDeleteWarrantyRequest() : setDeleteWarrantyRequestDialogOpen(false) }}
                context={
                    <div className="flex flex-wrap justify-center">
                        <Input multiline={false} help_text={""} label_text={"deleted reason"} value={deletedReason} onTextChange={(text: string) => setDeletedReason(text)} type="text" width={40} />
                    </div>
                }
            />
        </div>
    );
};
export default Warranty;